/**
 * ------------------------------------------------------------------------------------------------------------
 * ARCHITECTURAL NOTE:
 * KHÔNG sử dụng file index.ts (Barrel) trong thư mục @core để tránh Circular Dependency (NG0200).
 * Luôn import trực tiếp từ file nguồn.
 * ------------------------------------------------------------------------------------------------------------
 */
import {DOCUMENT, Inject, Injectable, Injector, OnDestroy, PLATFORM_ID} from '@angular/core';
import {BehaviorSubject, catchError, concatMap, Observable, of, Subject, take, timeout} from "rxjs";
import {HttpClient} from '@angular/common/http';
// CHÚ Ý: Luôn import trực tiếp từ file nguồn (ví dụ: ./app.service),
// KHÔNG import từ barrel "@core/services" để tránh lỗi Circular Dependency.
import {AppService} from "@core/services/app.service";
import {SocketIoService} from "@core/services/socket-io.service";
import {DurableObjectService} from "@core/realtime/durable-object.service";

import {Router} from "@angular/router";
import {isPlatformBrowser} from "@angular/common";
import {PermissionService} from "./permission.service";

import {STORAGE_KEY} from '@core/constants';
import {IUserHeader, ROLES} from '@shared/models/user';
import {environment} from '@environments/environment';
import {isArray, parseParams} from '@shared/utils';
import {IBusinessOption} from '@app/shared/models';
import {ROLES_NEW} from './auth.constants';


export interface IPage {
  id: any;
  businessId?: any;
  channel?: any;
  name?: string;
  avatar?: string;
  token?: string;
  isSyncedData?: any;
  settings?: any;
  isManagePage?: boolean;
  /** Facebook Conversions API dataset ID — dùng để kiểm tra kết nối Meta CAPI */
  fbDatasetId?: string;
  zaloMobile?: string;
  aliasName?: string;
  /** Facebook Page cha liên kết với Instagram Page (extension cần khi tra avatar/UID). */
  pageParentId?: string | null;
}

/**
 * Chuẩn hoá authData BE trả về: /auth/me trả `data` là chuỗi JSON (BE removePermsData), các API khác trả object.
 * Không parse thì auth bị lưu thành chuỗi → mất uid/jwt → FE tưởng đã đăng xuất trong khi phiên BE vẫn còn.
 */
export function parseAuthData(data: unknown): Auth | null {
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch {
      return null;
    }
  }
  return data && typeof data === 'object' && !Array.isArray(data) ? data as Auth : null;
}

export interface Auth {
  trackingInsta?: any;
  uid?: string;
  posUid?: string;
  displayName?: string;
  fbUid?: string;
  fbName?: string;
  createdTime?: string;
  announcements?: string;
  /** Thời gian đăng của thông báo hệ thống được xem lần cuối (popup tự động ở màn hội thoại). */
  announcementCreatedTime?: string | number;
  role?: number;
  userType?: number;
  jwt?: string;
  settings?: any;
  business?: any;
  businesses?: any;
  // Các doanh nghiệp có quyền sử dụng
  mBids?: Array<any>;
  // Tất cả các doanh nghiệp liên quan (luôn lưu dưới dạng mảng ID)
  aBids?: Array<any>;
  bids?: string;
  // Tất cả các pages
  pageIds?: Array<any>;
  pages?: {
    [key: string]: IPage
  };
}

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  protected auth: Auth = {};
  private _isLoggingOut = false;
  /** Lý do đăng xuất tự động của lần logout đang chạy — lưu lại để hiển thị sau khi trang tải lại */
  private _logoutReason = '';
  private readonly _onAuthCleared$ = new Subject<void>();
  private readonly handleStorageChange = (event: StorageEvent): void => {
    if (this._isLoggingOut || !this.getAuthorizationToken()) return;
    if (event.key !== null && event.key !== STORAGE_KEY.usr) return;

    try {
      if (event.storageArea !== localStorage) return;
      const storedUser = localStorage.getItem(STORAGE_KEY.usr);
      if (storedUser) {
        const nextAuth = JSON.parse(storedUser) as Auth;
        if (!nextAuth?.jwt || (nextAuth.jwt === this.auth.jwt && nextAuth.uid === this.auth.uid)) return;
        // Tab khác đã nhận phiên mới: reload để lấy đúng auth, không xóa usr mới.
        this._isLoggingOut = true;
        this.resetAuthState();
        this.document.location.reload();
        return;
      }
    } catch {
      return;
    }
    // Tab đã logout chịu trách nhiệm hủy phiên BE, các tab còn lại chỉ dọn FE.
    this._isLoggingOut = true;
    this.finishLogout(false);
  };

  // Chứa Page mà hội thoại đang xem => để biết hội thoại thuộc page nào.
  private _pageDisplay: any = new BehaviorSubject('');
  private _isSelectPage = new BehaviorSubject(false);
  private _activeBusiness = new BehaviorSubject<IBusinessOption | null>(null);
  // Danh sách các doanh nghiệp mà người dùng có quyền quản lý
  private _businesses = new BehaviorSubject<IBusinessOption[]>([]);
  /** Tín hiệu phát khi auth thay đổi (chọn page, đổi business, ...) — services subscribe để reset + reload */
  private _onAuthChanged$ = new Subject<void>();

  /**
   * Doanh nghiệp hiện tại đang hoạt động (Sử dụng interface IBusinessOption)
   */
  public get businessCurrent(): IBusinessOption | null {
    return this._activeBusiness.value;
  }

  public set businessCurrent(value: IBusinessOption | null | any) {
    this.activeBusiness = value;
  }

  constructor(
    private _app: AppService,
    @Inject(DOCUMENT) private document: any,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: object,
    private io: SocketIoService,
    private http: HttpClient,
    private injector: Injector,
    private doService: DurableObjectService,
  ) {
    // Tự động khôi phục phiên làm việc từ localStorage khi ứng dụng khởi chạy
    if (isPlatformBrowser(this.platformId)) {
      this.restoreSession();
      this.document.defaultView?.addEventListener('storage', this.handleStorageChange);
    }
  }

  ngOnDestroy(): void {
    this.document.defaultView?.removeEventListener('storage', this.handleStorageChange);
  }

  get isLoggingOut(): boolean {
    return this._isLoggingOut;
  }

  /** Hủy request và cache đang thuộc phiên cũ trước khi nhận phiên mới. */
  get onAuthCleared$() {
    return this._onAuthCleared$.asObservable();
  }

  /**
   * Khôi phục session từ localStorage
   */
  private restoreSession() {
    try {
      const usr = localStorage.getItem(STORAGE_KEY.usr);
      if (usr) {
        const authData = JSON.parse(usr);
        if (!authData || typeof authData !== 'object' || Array.isArray(authData)
          || typeof authData.jwt !== 'string' || !authData.jwt.trim()) {
          this.clearAuthUser();
          return;
        }
        this.setAuthDataService(authData);
        // Nếu aBids trước đó bị lưu dạng chuỗi string trong localStorage, cập nhật lại thành mảng sạch
        if (typeof authData?.aBids === 'string' && Array.isArray(this.auth?.aBids)) {
          localStorage.setItem(STORAGE_KEY.usr, JSON.stringify(this.auth));
        }
      }
    } catch (e) {
      console.error('AuthService: Không thể khôi phục session', e);
      this.clearAuthUser();
    }
  }

  /**
   * Lấy doanh nghiệp đang hoạt động (giá trị hiện tại)
   */
  get activeBusiness(): IBusinessOption | null {
    return this._activeBusiness.value;
  }

  /**
   * Stream dữ liệu doanh nghiệp đang hoạt động để các component có thể subscribe
   */
  get activeBusiness$() {
    return this._activeBusiness.asObservable();
  }

  /**
   * Stream danh sách doanh nghiệp có quyền quản lý
   */
  get businesses$() {
    return this._businesses.asObservable();
  }

  /**
   * Tín hiệu phát khi dữ liệu auth thay đổi (chọn page, đổi business).
   * Services (tags, quick message, settings, ...) subscribe để reset + reload dữ liệu.
   */
  get onAuthChanged$() {
    return this._onAuthChanged$.asObservable();
  }


  /**
   * Thiết lập doanh nghiệp đang hoạt động
   * @param business Thông tin doanh nghiệp hoặc null
   */
  set activeBusiness(business: IBusinessOption | null | any) {
    if (this._isLoggingOut) return;
    // Cập nhật giá trị mới vào Subject
    this._activeBusiness.next(business);

    // Lưu vào localStorage để duy trì trạng thái khi reload trang
    if (isPlatformBrowser(this.platformId) && business) {
      localStorage.setItem('activeBusiness', JSON.stringify(business));
    }

    // Thông báo cho các service biết auth đã thay đổi để reset + reload dữ liệu
    this._onAuthChanged$.next();
  }

  /** Xóa dữ liệu tài khoản, giữ ngôn ngữ, theme và các cờ của ứng dụng mobile. */
  clearAuthUser(): void {
    this.resetAuthState();
    if (isPlatformBrowser(this.platformId)) {
      // usr được xóa cuối để tab khác chỉ nhận logout sau khi context đã được dọn.
      const accountKeys = [STORAGE_KEY.accessToken, STORAGE_KEY.userData,
        STORAGE_KEY.businesses, STORAGE_KEY.BUSINESS_CURRENT, 'activeBusiness',
        'searchCustomer', STORAGE_KEY.usr];
      for (const key of accountKeys) {
        try {
          localStorage.removeItem(key);
        } catch (error) {
          console.warn('AuthService: Không thể xóa cache tài khoản', key, error);
        }
      }
      for (const key of ['filterPageIds', 'pagesPartnerAfiliate']) {
        try {
          sessionStorage.removeItem(key);
        } catch (error) {
          console.warn('AuthService: Không thể xóa context Page', key, error);
        }
      }
    }
  }

  private resetAuthState(): void {
    // Một kết nối lỗi không được cản việc đóng các kết nối còn lại.
    const cleanupRooms = [
      () => this.io.leaveRoom(this.getPageIds()),
      () => this.io.leaveRoom((this.auth.mBids ?? []).map(id => String(id))),
      () => this.io.leaveAffRoom(),
      () => this.doService.leaveAllRooms(),
    ];
    for (const cleanup of cleanupRooms) {
      try {
        cleanup();
      } catch (error) {
        console.warn('AuthService: Không thể đóng room của user cũ', error);
      }
    }
    this.auth = {};
    this._pageDisplay.next('');
    this._isSelectPage.next(false);
    this._activeBusiness.next(null);
    this._businesses.next([]);
    this.injector.get(PermissionService).clearPermissions();
    this._onAuthCleared$.next();
    this._onAuthChanged$.next();
  }

  /** Phục hồi lỗi login 403 dùng cùng cách reset với guard, không xóa toàn bộ storage. */
  clearCachedAuthUser(): void {
    this.clearAuthUser();
  }

  /**
   * @param reason Lý do khi bị đăng xuất tự động (khoá tài khoản, phiên hết hạn...). Logout tải lại trang nên toast
   *               mất ngay — lý do được giữ qua lần tải lại để AppComponent hiển thị bằng hộp thoại.
   */
  logout(reason = ''): void {
    if (this._isLoggingOut) return;
    this._isLoggingOut = true;
    this._logoutReason = reason;
    this._app.showLoading();

    // App mobile: gỡ topic + xóa token FCM khi session BE còn sống (API yêu cầu đăng nhập),
    // lỗi/timeout vẫn tiếp tục logout để người dùng không bị kẹt.
    this.unsubscribeFcmToken().pipe(
      // Giữ token tới khi POST hoàn tất để BE vẫn kiểm tra được CSRF.
      // Timeout 15s riêng cho POST logout như trước; bước hủy FCM có timeout 5s riêng.
      concatMap(() => this.http.post<{code: number}>(environment.baseUrlApi + '/auth/logout', {}).pipe(timeout(15000))),
      take(1),
    ).subscribe({
      next: response => {
        this.finishLogout(Number(response?.code) !== 1);
      },
      error: (err) => {
        console.error('Lỗi khi gọi API logout:', err);
        // GET logout có sẵn ở BE hủy session và redirect, không cần token CSRF.
        // Không chỉ về trang chủ vì phiên BE có thể vẫn còn khi POST thất bại.
        this.finishLogout(true);
      }
    });
  }

  /**
   * Hủy đăng ký nhận thông báo của thiết bị (token FCM do app mobile gửi vào `tokenApp`).
   * Port từ FE13 db.service.ts unSubscribeNotification(). Không có token → bỏ qua.
   * Giữ `tokenApp` trong localStorage vì đó là token của thiết bị, cần dùng lại khi đăng nhập lại.
   */
  private unsubscribeFcmToken(): Observable<unknown> {
    let tokenApp: string | null = null;
    if (isPlatformBrowser(this.platformId)) {
      try {
        tokenApp = localStorage.getItem('tokenApp');
      } catch { /* Trình duyệt chặn storage → không có token để hủy. */ }
    }
    if (!tokenApp) return of(null);

    // Gửi form-urlencoded giống LoginApiService.unsubscribeNotification (dùng ở outPages).
    return this.http.post(environment.baseUrlApi + '/notification/unsubscribefromalltopics', parseParams({token: tokenApp})).pipe(
      timeout(5000),
      catchError(err => {
        console.warn('AuthService: Không thể hủy token FCM khi logout', err);
        return of(null);
      }),
    );
  }

  private finishLogout(useServerLogout: boolean): void {
    let isMobileApp = environment.isMobileLayout;
    if (isPlatformBrowser(this.platformId)) {
      try {
        isMobileApp = isMobileApp || !!localStorage.getItem('isMobileApp');
      } catch { /* Vẫn logout được khi trình duyệt chặn storage. */ }
    }
    this.clearAuthUser();
    if (this._logoutReason && isPlatformBrowser(this.platformId)) {
      try {
        sessionStorage.setItem(STORAGE_KEY.logoutReason, this._logoutReason);
      } catch { /* Trình duyệt chặn storage → chỉ còn toast trước khi tải lại. */ }
    }
    this.document.location.href = useServerLogout
      ? environment.baseUrlApi + '/auth/logout' + (isMobileApp ? '?isMobileApp=1' : '')
      : isMobileApp ? '/login-appmobile?logout=1' : '/';
  }

  /** Lấy rồi xoá lý do của lần bị đăng xuất tự động trước (trang vừa tải lại sau logout) */
  takeLogoutReason(): string {
    if (!isPlatformBrowser(this.platformId)) return '';
    try {
      const reason = sessionStorage.getItem(STORAGE_KEY.logoutReason) ?? '';
      sessionStorage.removeItem(STORAGE_KEY.logoutReason);
      return reason;
    } catch {
      return '';
    }
  }

  isLoginEd = () => {
    return !this._isLoggingOut && !!this.getAuthorizationToken();
  }

  hasJoinPages(): boolean {
    // Kiểm tra xem user đã chọn page hay chưa thông qua pageIds hoặc danh sách pages
    const hasIds = this.auth?.pageIds && isArray(this.auth.pageIds) && this.auth.pageIds.length > 0;
    const hasPages = this.auth?.pages && (isArray(this.auth.pages) && this.auth.pages.length > 0 || typeof this.auth.pages === 'object' && Object.keys(this.auth.pages).length > 0);
    return !!(hasIds || hasPages);
  }

  /**
   * Cập nhật dữ liệu Authentication vào hệ thống
   * @param data Đối tượng Auth chứa JWT, User info, Businesses...
   */
  setAuthDataService(data: Auth) {
    if (!data || this._isLoggingOut) return;

    // 1. Chuẩn hóa dữ liệu (Đảm bảo businesses luôn là mảng)
    this.normalizeAuthData(data);
    this.auth = data;

    // 2. Đồng bộ Quyền (Permissions) ngay lập tức
    // Sử dụng injector để tránh vòng lặp phụ thuộc (Circular Dependency)
    if (this.auth.role !== undefined) {
      const permissionService = this.injector.get(PermissionService);
      permissionService.setPermissionsByRole(this.auth.role);
    }

    // 3. Thông báo cho các thành phần UI (Header, Menu) - Đồng bộ chuẩn v1: dùng getBusinesses() để tài khoản nhân viên vẫn hiển thị doanh nghiệp
    this._businesses.next(this.getBusinesses());

    // 4. Kiểm tra trạng thái chọn Page để hiển thị Menu
    if (this.hasJoinPages()) {
      this.isSelectPage = true;
    }

    // 5. Khởi tạo doanh nghiệp mặc định
    this.initializeActiveBusiness();

    // 6. Kết nối Durable Object cho user / page / business room.
    // Khi `environment.durable_object.ws_url` rỗng, service tự no-op.
    // @see [ADR-0002](../../../../agent-vpage/tracking/decision-log.md)
    this.syncDurableObjectRooms();
  }

  /**
   * Đồng bộ các DO room theo trạng thái auth hiện tại.
   * Room của Page/Business đã bị bỏ quyền sẽ được đóng, không chỉ join thêm room mới.
   */
  private syncDurableObjectRooms(): void {
    try {
      if (!this.doService.isConfigured()) return;

      this.doService.syncAuthRooms({
        userId: this.auth?.uid,
        pageIds: (this.auth?.pageIds ?? []) as Array<string | number>,
        // Như FE13 và room socket.io: mọi doanh nghiệp người dùng thuộc về (aBids), không chỉ doanh nghiệp
        // quản lý (mBids) — nhân viên cũng phải gửi/nhận sự kiện doanh nghiệp (Cài đặt nhãn, tin nhắn nhanh theo nhóm...).
        businessIds: [...this.getBusinessAllSelectManagement(), ...(this.auth?.mBids ?? [])] as Array<string | number>,
      });
    } catch (err) {
      // Không bao giờ chặn flow auth vì lỗi DO.
      console.warn('[AuthService] syncDurableObjectRooms failed:', err);
    }
  }

  /**
   * Chuẩn hóa dữ liệu Auth (Xử lý trường hợp Backend trả về Object thay vì Array cho businesses, hoặc aBids là string)
   */
  private normalizeAuthData(data: Auth) {
    if (data.businesses && !isArray(data.businesses)) {
      data.businesses = Object.values(data.businesses);
    }
    // Chuẩn hóa aBids về mảng nếu đang bị lưu dạng string (hoặc number đơn lẻ) để tương thích chuẩn v1 (chat-ui)
    if (data.aBids && !Array.isArray(data.aBids)) {
      if (typeof data.aBids === 'string') {
        const parsed = (data.aBids as string).includes(',')
          ? (data.aBids as string).split(',').map(s => s.trim()).filter(Boolean)
          : [(data.aBids as string).trim()].filter(Boolean);
        data.aBids = parsed.map(id => Number(id) || id);
      } else {
        data.aBids = [data.aBids];
      }
    }
  }


  setAuthorization(data: string): void {
    if (this._isLoggingOut) return;
    const auth = JSON.parse(data) as Auth;
    this.auth = auth;
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(STORAGE_KEY.usr, data);
    }

    // Auth callback có thể ghi trực tiếp bản auth đầy đủ, không đi qua
    // setAuthDataService(). Khi đó vẫn phải thay room cũ bằng đúng tập quyền mới.
    if (auth.pages || auth.pageIds || auth.businesses || auth.mBids) {
      this.normalizeAuthData(auth);
      // Đồng bộ chuẩn v1: dùng getBusinesses() để cập nhật danh sách doanh nghiệp cho UI
      this._businesses.next(this.getBusinesses());
      this.initializeActiveBusiness();
      this.syncDurableObjectRooms();
      this._onAuthChanged$.next();
    }
  }

  updateAuthorization(data: any) {
    if (this._isLoggingOut || !this.getAuthorizationToken()) return;
    // /business/page/joinpages trả JWT mới kèm context Page. Phải thay token
    // trước request kế tiếp, nếu không BE vẫn nhận session Page cũ/null.
    if (Object.prototype.hasOwnProperty.call(data ?? {}, 'jwt')) {
      this.auth.jwt = data.jwt;
    }

    // Giữ parity với FE13 cho các thuộc tính user cũng được BE trả lại khi
    // chọn Page. Dùng hasOwnProperty để không bỏ qua các giá trị hợp lệ như 0.
    if (Object.prototype.hasOwnProperty.call(data ?? {}, 'posUid')) {
      this.auth.posUid = data.posUid;
    }
    if (Object.prototype.hasOwnProperty.call(data ?? {}, 'displayName')) {
      this.auth.displayName = data.displayName;
    }

    if (data?.businesses && data?.pages) {
      this.auth.pages = {};

      for (const page of data.pages) {
        const p: any = {
          id: page.id,
          channel: page.channel,
          name: page.name,
          isManagePage: page.isManagePage,
          isSyncedData: page.isSyncedData
        };

        if (page?.avatar) p.avatar = page.avatar;
        if (page?.token) p.token = page.token;
        if (page?.fbDatasetId) p.fbDatasetId = page.fbDatasetId;
        if (page?.pageParentId) p.pageParentId = page.pageParentId;
        if (page?.settings) p.settings = page.settings;
        if (page?.aliasName) p.aliasName = page.aliasName;
        if (page?.zaloMobile) p.zaloMobile = page.zaloMobile;

        let bkey = 'other';
        if (page.businessId) {
          bkey = page.businessId;

          p.businessId = page.businessId.toString();
        }

        this.auth.pages[page.id] = p;

        data.businesses[bkey].pages.push({
          id: page.id,
          name: page.name,
          isManagePage: page.isManagePage,
          channel: page.channel
        });
      }

      this.auth.businesses = [];
      for (const bid in data.businesses) {
        this.auth.businesses.push(data.businesses[bid]);
      }
    }

    if (data?.aBids) {
      if (Array.isArray(data.aBids)) {
        this.auth.aBids = data.aBids;
      } else if (typeof data.aBids === 'string') {
        const parsed = data.aBids.includes(',')
          ? data.aBids.split(',').map((s: string) => s.trim()).filter(Boolean)
          : [data.aBids.trim()].filter(Boolean);
        this.auth.aBids = parsed.map((id: string) => Number(id) || id);
      } else {
        this.auth.aBids = [data.aBids];
      }
      this.io.joinRoom(this.getBusinessAllSelectManagement());
    }

    if (data?.pageIds) {
      this.auth.pageIds = data.pageIds;
      // Join room on socketIO
      this.io.joinRoom(this.getPageIds());
    }

    if (data?.settings) {
      this.auth.settings = data.settings;
    }

    if (data?.role) {
      this.auth.role = data.role;
    }

    // Cập nhật lại mBids nếu có (danh sách ID doanh nghiệp quản lý)
    if (data?.mBids) {
      this.auth.mBids = data.mBids;
    }

    // Cập nhật danh sách doanh nghiệp mới vào Subject để báo cho Header/UI biết (chuẩn v1: dùng getBusinesses)
    this._businesses.next(this.getBusinesses());

    // Đồng bộ lại doanh nghiệp đang hoạt động để Header cập nhật đúng thông tin
    this.initializeActiveBusiness();

    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem('usr', JSON.stringify(this.auth));
    }

    // updateAuthorization được gọi sau cấp/chọn lại quyền Page. Đồng bộ theo
    // tập mới để đóng DO subscriptions của page/business không còn được chọn.
    this.syncDurableObjectRooms();

    // Thông báo cho các service biết auth đã thay đổi để reset + reload dữ liệu (tags, quick msg, settings, ...)
    this._onAuthChanged$.next();
  }

  /**
   * Chuyển Super Admin sang context của đúng một Page sau `/auth/pagecontrol`.
   * Backend đã mở session Page tại thời điểm API thành công; state dưới đây
   * chỉ phản chiếu context đó cho UI, socket và lần tải lại tiếp theo.
   */
  setPageControl(page: IPage, settings?: unknown): void {
    if (!this.isSuperAdmin() || !page?.id) return;

    const previousPageIds = this.getPageIds().map(id => String(id));
    const previousBusinessIds = (this.auth.mBids ?? []).map(id => String(id));
    const pageId = String(page.id);
    const businessId = String(page.businessId ?? '').trim();
    const numericBusinessId = Number(businessId);
    const hasBusiness = Boolean(businessId) && Number.isFinite(numericBusinessId) && numericBusinessId > 0;

    // Không nhận realtime của context cũ sau khi đã điều khiển sang Page mới.
    if (previousPageIds.length) this.io.leaveRoom(previousPageIds);
    if (previousBusinessIds.length) this.io.leaveRoom(previousBusinessIds);

    const controlledPage: IPage = {
      id: pageId,
      name: page.name || pageId,
      channel: page.channel,
      businessId: hasBusiness ? String(numericBusinessId) : undefined,
      avatar: page.avatar,
      isManagePage: true,
      isSyncedData: page.isSyncedData,
      settings: settings ?? page.settings,
    };
    this.auth.pageIds = [pageId];
    this.auth.pages = {[pageId]: controlledPage};

    let controlledBusiness: IBusinessOption | null = null;
    if (hasBusiness) {
      controlledBusiness = {
        id: numericBusinessId,
        name: `DN: ${numericBusinessId}`,
        roleId: ROLES_NEW.SYSTEM_ROLE_SUPER_ADMIN,
        pageIds: [pageId],
        pages: [{id: pageId, name: controlledPage.name, channel: controlledPage.channel, isManagePage: true}],
      };
      this.auth.businesses = [controlledBusiness];
      this.auth.mBids = [numericBusinessId];
      this.auth.aBids = [numericBusinessId];
    } else {
      this.auth.businesses = [];
      this.auth.mBids = [];
      this.auth.aBids = [];
    }

    this._activeBusiness.next(controlledBusiness);
    this.isSelectPage = true;
    this._businesses.next(this.getBusinesses());

    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(STORAGE_KEY.usr, JSON.stringify(this.auth));
      if (controlledBusiness) {
        localStorage.setItem('activeBusiness', JSON.stringify(controlledBusiness));
      } else {
        localStorage.removeItem('activeBusiness');
      }
    }

    this.io.joinRoom([pageId]);
    if (controlledBusiness) this.io.joinRoom([String(controlledBusiness.id)]);
    this.syncDurableObjectRooms();
    this._onAuthChanged$.next();
  }

  /**
   * Kiểm tra xem trong auth.pages hiện tại đã có dữ liệu trang của doanh nghiệp này chưa.
   * Dùng để tránh gọi API tải lại pages khi admin đã ở đúng context.
   *
   * @param businessId ID doanh nghiệp cần kiểm tra
   */
  hasPagesOfBusiness(businessId: number | string): boolean {
    if (!this.auth?.pages || typeof this.auth.pages !== 'object') {
      return false;
    }
    const pages = Object.values(this.auth.pages);
    if (!pages.length) return false;
    return pages.some(p => String(p.businessId) === String(businessId));
  }

  /**
   * Thiết lập ngữ cảnh doanh nghiệp cho Admin/SuperAdmin khi truy cập qua param ?businessId=xxx
   * Mô phỏng đăng nhập như Giám đốc của doanh nghiệp đó và tự động chọn toàn bộ các page để lưu vào auth.
   * TUYỆT ĐỐI CHỈ ÁP DỤNG CHO ADMIN/SUPERADMIN, không ảnh hưởng đến user thường.
   *
   * @param businessId ID của doanh nghiệp cần debug/xem
   * @param businessName Tên của doanh nghiệp
   * @param pages Danh sách các page thuộc doanh nghiệp đó
   */
  setAdminImpersonateBusiness(businessId: number | string, businessName: string, pages: IPage[]): void {
    // 1. Kiểm tra an toàn: Chỉ thực hiện khi người dùng có quyền Admin hệ thống
    if (!this.isAdmin()) {
      return;
    }

    const bIdNum = Number(businessId);
    if (!Number.isFinite(bIdNum) || bIdNum <= 0) {
      return;
    }
    const bIdStr = String(bIdNum);

    // 2. Chuẩn bị mảng pageIds và map pages vào auth.pages
    const pageIds: string[] = [];
    this.auth.pages = {};

    const businessPages: any[] = [];
    if (pages && Array.isArray(pages)) {
      for (const p of pages) {
        if (!p?.id) continue;
        const pageId = String(p.id);
        pageIds.push(pageId);

        const pageItem: IPage = {
          id: pageId,
          name: p.name || pageId,
          channel: p.channel,
          businessId: bIdStr,
          avatar: p.avatar,
          isManagePage: true,
          isSyncedData: true,
          aliasName: p.aliasName,
          zaloMobile: p.zaloMobile,
        };

        this.auth.pages[pageId] = pageItem;

        businessPages.push({
          id: pageId,
          name: p.name || pageId,
          channel: p.channel,
          isManagePage: true,
        });
      }
    }

    // 4. Cập nhật mảng businesses của auth (đặt roleId là Giám đốc doanh nghiệp)
    const targetBusiness: IBusinessOption = {
      id: bIdNum,
      name: businessName || `Doanh nghiệp ${bIdNum}`,
      roleId: 'SYSTEM_ROLE_MANAGER', // Quyền Giám đốc doanh nghiệp
      pageIds: pageIds,
      pages: businessPages,
      isImpersonated: true, // Đánh dấu rõ ràng đây là doanh nghiệp do Admin impersonate (không phải login gốc)
    };

    let businesses = this.getBusinesses();
    if (!isArray(businesses)) {
      businesses = [];
    }

    // Tìm xem doanh nghiệp đã có trong danh sách chưa, nếu có thì thay thế, chưa có thì thêm vào
    const existingIndex = businesses.findIndex((b: any) => b.id == bIdNum);
    if (existingIndex >= 0) {
      businesses[existingIndex] = targetBusiness;
    } else {
      businesses.push(targetBusiness);
    }
    this.auth.businesses = businesses;

    // 5. Cập nhật các trường định danh doanh nghiệp & pages vào auth
    this.auth.pageIds = pageIds;
    // aBids bắt buộc lưu dạng mảng chứa ID doanh nghiệp (để tương thích hoàn toàn với FE v1 chat-ui và tránh lỗi w.forEach is not a function)
    this.auth.aBids = [bIdNum];
    this.auth.mBids = [bIdNum];

    // 6. Cập nhật activeBusiness
    this._activeBusiness.next(targetBusiness);

    // 7. Cập nhật cờ chọn page sang true để hiển thị đầy đủ menu
    this.isSelectPage = true;

    // 8. Cập nhật danh sách doanh nghiệp để Header hiển thị
    this._businesses.next(this.getBusinesses());

    // 9. Lưu vào localStorage để duy trì trạng thái khi reload
    if (isPlatformBrowser(this.platformId)) {
      try {
        localStorage.setItem(STORAGE_KEY.usr, JSON.stringify(this.auth));
        localStorage.setItem('activeBusiness', JSON.stringify(targetBusiness));
      } catch (e) {
        console.error('[AuthService] Lỗi khi lưu auth data vào localStorage:', e);
      }
    }

    // 10. Đồng bộ phòng Durable Object / Socket
    try {
      if (pageIds.length > 0) {
        this.io.joinRoom(pageIds);
      }
      this.io.joinRoom([bIdStr]);
      this.syncDurableObjectRooms();
    } catch (e) {
      console.warn('[AuthService] Lỗi khi join socket rooms cho admin impersonate:', e);
    }

    // 11. Phát tín hiệu thông báo cho toàn bộ các component/services reload dữ liệu
    this._onAuthChanged$.next();
  }

  /**
   * Kiểm tra xem doanh nghiệp này Admin đã login (đã có quyền và đã chọn page lúc login) hay chưa.
   * - Nếu đã login: khi gõ ?businessId=X sẽ lấy đúng các page đang chọn lúc login, không kích hoạt load toàn bộ page.
   * - Nếu chưa login (doanh nghiệp lạ): mới kích hoạt tính năng của Admin để load toàn bộ page cho admin check.
   *
   * @param businessId ID doanh nghiệp cần kiểm tra
   */
  isBusinessLoggedIn(businessId: number | string): boolean {
    const bIdNum = Number(businessId);
    if (!Number.isFinite(bIdNum) || bIdNum <= 0) {
      return false;
    }

    const businesses = this.getBusinesses();
    if (!Array.isArray(businesses) || businesses.length === 0) {
      return false;
    }

    // 1. Tìm doanh nghiệp trong danh sách doanh nghiệp của user
    const biz = businesses.find((b: any) => Number(b.id) === bIdNum);
    if (!biz) {
      return false;
    }

    // 2. Nếu doanh nghiệp này được đánh dấu là do Admin impersonate -> Đây là doanh nghiệp Admin KHÔNG login gốc
    if ((biz as any).isImpersonated) {
      return false;
    }

    // 3. Doanh nghiệp này phải có danh sách page đã chọn lúc login (hoặc có page trong auth.pages)
    const hasPages = Array.isArray(biz.pages) && biz.pages.length > 0;
    const hasPagesInAuth = this.hasPagesOfBusiness(bIdNum);

    return hasPages || hasPagesInAuth;
  }

  /**
   * Khôi phục ngữ cảnh làm việc cho doanh nghiệp mà Admin đã login bình thường (chọn page lúc login).
   * Lấy lại đúng các page đang chọn như bình thường của doanh nghiệp này, không load lại toàn bộ page.
   *
   * @param businessId ID doanh nghiệp đã login
   */
  restoreLoggedInBusiness(businessId: number | string): void {
    const bIdNum = Number(businessId);
    if (!Number.isFinite(bIdNum) || bIdNum <= 0) {
      return;
    }
    const bIdStr = String(bIdNum);

    const businesses = this.getBusinesses();
    const targetBiz = businesses.find((b: any) => Number(b.id) === bIdNum);
    if (!targetBiz) {
      return;
    }

    // Nếu đã ở đúng doanh nghiệp này và đã có các page của doanh nghiệp này -> không cần cập nhật lặp lại
    if (Number(this.activeBusiness?.id) === bIdNum && this.hasPagesOfBusiness(bIdNum)) {
      return;
    }

    // 1. Dọn dẹp: Nếu trước đó có doanh nghiệp impersonate thì loại bỏ các page của doanh nghiệp impersonate đó
    const impersonatedBizs = businesses.filter((b: any) => (b as any).isImpersonated);
    if (impersonatedBizs.length > 0) {
      const impIds = new Set(impersonatedBizs.map((b: any) => String(b.id)));
      if (this.auth.pages && typeof this.auth.pages === 'object') {
        Object.keys(this.auth.pages).forEach(pId => {
          if (impIds.has(String(this.auth.pages![pId]?.businessId))) {
            delete this.auth.pages![pId];
          }
        });
      }
      // Loại bỏ doanh nghiệp impersonate khỏi danh sách businesses
      this.auth.businesses = businesses.filter((b: any) => !(b as any).isImpersonated);
    }

    // 2. Lấy danh sách các trang gốc đã chọn lúc login của doanh nghiệp này
    const bizPages: any[] = Array.isArray(targetBiz.pages) ? targetBiz.pages : [];
    const pageIds: string[] = [];

    // Đảm bảo auth.pages tồn tại
    if (!this.auth.pages || typeof this.auth.pages !== 'object') {
      this.auth.pages = {};
    }

    // 3. Nạp lại các trang gốc của doanh nghiệp này vào auth.pages
    if (bizPages.length > 0) {
      for (const p of bizPages) {
        if (!p?.id) continue;
        const pageId = String(p.id);
        pageIds.push(pageId);

        this.auth.pages[pageId] = {
          id: pageId,
          name: p.name || pageId,
          channel: p.channel != null ? Number(p.channel) : 1,
          businessId: bIdStr,
          avatar: p.avatar,
          isManagePage: true,
          isSyncedData: true,
          aliasName: p.aliasName,
          zaloMobile: p.zaloMobile,
        };
      }
    }

    // 3. Cập nhật các trường định danh trong auth
    if (pageIds.length > 0) {
      this.auth.pageIds = pageIds;
    }
    this.auth.aBids = [bIdNum];
    this.auth.mBids = [bIdNum];

    // 4. Cập nhật activeBusiness
    this._activeBusiness.next(targetBiz);
    this.isSelectPage = true;
    this._businesses.next(this.getBusinesses());

    // 5. Lưu vào localStorage để duy trì trạng thái khi reload
    if (isPlatformBrowser(this.platformId)) {
      try {
        localStorage.setItem(STORAGE_KEY.usr, JSON.stringify(this.auth));
        localStorage.setItem('activeBusiness', JSON.stringify(targetBiz));
      } catch (e) {
        console.error('[AuthService] Lỗi khi lưu auth data vào localStorage:', e);
      }
    }

    // 6. Join rooms
    try {
      if (pageIds.length > 0) {
        this.io.joinRoom(pageIds);
      }
      this.io.joinRoom([bIdStr]);
      this.syncDurableObjectRooms();
    } catch (e) {
      console.warn('[AuthService] Lỗi khi join socket rooms cho doanh nghiệp đã login:', e);
    }

    // 7. Báo cho toàn hệ thống cập nhật dữ liệu
    this._onAuthChanged$.next();
  }

  getUserType() {
    return this.auth.userType;
  }

  getPosUid(): string {
    return this.auth?.posUid ?? '';
  }

  // Kiểm tra người dùng có quản lý doanh nghiệp nào không
  isBusinessManagement() {
    return !!this.auth.mBids?.length;
  }

  /**
   * Lấy danh sách ID những doanh nghiệp đã chọn khi chọn page (luôn là mảng)
   */
  getBusinessAllSelectManagement(): any[] {
    if (!this.auth?.aBids) {
      return [];
    }
    return Array.isArray(this.auth.aBids) ? this.auth.aBids : [this.auth.aBids];
  }

  getBusinessesManagement(): IBusinessOption[] {
    // Guard: kiểm tra businesses tồn tại trước khi xử lý
    if (!this.auth?.businesses) {
      return [];
    }

    const businessesSource = isArray(this.auth.businesses) ? this.auth.businesses : Object.values(this.auth.businesses);

    // Nếu là Admin/SuperAdmin thì trả về toàn bộ danh sách doanh nghiệp
    if (this.isAdmin()) {
      return businessesSource as IBusinessOption[];
    }

    if (!this.auth?.mBids || !isArray(this.auth.mBids))
      return [];

    const businesses: IBusinessOption[] = [];
    for (const mBid of this.auth.mBids) {
      for (const business of businessesSource as any[]) {
        if (business.id == mBid) {
          businesses.push(business);
        }
      }
    }

    return businesses;
  }

  /** Toàn bộ doanh nghiệp gắn với user, không giới hạn theo quyền quản lý. */
  getBusinesses(): IBusinessOption[] {
    if (!this.auth?.businesses) return [];
    return isArray(this.auth.businesses)
      ? this.auth.businesses as IBusinessOption[]
      : Object.values(this.auth.businesses) as IBusinessOption[];
  }

  /**
   * Lấy danh sách các trang (Page) dựa trên channel và doanh nghiệp đang hoạt động.
   * @param channel (Tùy chọn) lọc theo channel cụ thể hoặc danh sách channel.
   * @returns Danh sách các IPage đã được lọc.
   */
  getPages(channel?: number | number[]): IPage[] {
    const pages: IPage[] = [];
    const activeBusiness = this.getActiveBusiness();

    if (this.auth && this.auth?.pages) {
      // 1. Chuyển object pages thành mảng để xử lý lọc
      let allPages = Object.values(this.auth.pages);

      // 2. Lọc theo doanh nghiệp đang chọn (activeBusiness) từ header
      if (activeBusiness) {
        allPages = allPages.filter(p => p.businessId == activeBusiness.id);
      }

      if (channel) {
        const channels = isArray(channel) ? (channel as number[]) : [channel as number];

        // 3. Lọc tiếp theo channel nếu có tham số truyền vào
        for (const cn of channels) {
          for (const page of allPages) {
            if (cn == page.channel) {
              pages.push(page);
            }
          }
        }
      } else {
        // Nếu không lọc channel, lấy toàn bộ danh sách đã lọc theo business
        pages.push(...allPages);
      }
    }

    return pages;
  }

  /** Cập nhật avatar page trong cache sau khi lấy được URL mới (URL CDN của Meta/TikTok có hạn). */
  setPageAvatar(pageId: string, avatar: string): void {
    const page = this.auth?.pages?.[pageId];
    if (!page || !avatar || page.avatar === avatar) return;

    page.avatar = avatar;
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(STORAGE_KEY.usr, JSON.stringify(this.auth));
    }
  }

  /** Cache Facebook Page cha của một Instagram page, giống session FE13. */
  setPageParentId(pageId: string, pageParentId: string | null): void {
    const page = this.auth?.pages?.[pageId];
    if (!page) return;

    page.pageParentId = pageParentId;
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(STORAGE_KEY.usr, JSON.stringify(this.auth));
    }
  }

  /**
   * Lấy danh sách ID của các trang.
   * @param channel (Tùy chọn) nếu truyền vào sẽ lọc theo channel 1 (Facebook)
   */
  getPageIds(channel?: any): string[] {
    if (channel) {
      if (this.auth?.pages) {
        return Object.values(this.auth.pages)
          .filter(page => page.channel === channel)
          .map(page => page.id);
      }
      return [];
    }
    return this.auth.pageIds ?? [];
  }

  /**
   * Lấy toàn bộ danh sách các trang mà người dùng đã chọn khi đăng nhập (không lọc theo activeBusiness).
   * Dùng cho modal chọn channel/page của module hội thoại/chat hoặc những nơi cần danh sách tổng.
   *
   * @param channel (Tùy chọn) lọc theo channel cụ thể hoặc danh sách channel.
   * @returns Danh sách tất cả IPage đã chọn khi login.
   */
  getAllPages(channel?: number | number[]): IPage[] {
    if (!this.auth?.pages) {
      return [];
    }
    const allPages = Object.values(this.auth.pages);
    if (!channel) {
      return allPages;
    }
    const channels = isArray(channel) ? (channel as number[]) : [channel as number];
    return allPages.filter(p => channels.includes(p.channel));
  }

  /**
   * Lấy businessId của page từ auth.pages (toàn bộ page đã chọn khi đăng nhập, KHÔNG lọc theo activeBusiness).
   * Parity FE13: authService.getBussinessId(pageId).
   *
   * @param pageId ID của page
   * @returns businessId dạng chuỗi, hoặc null nếu page không thuộc phiên đăng nhập
   */
  getBussinessId(pageId: string | number | null | undefined): string | null {
    if (pageId === null || pageId === undefined || pageId === '') return null;
    const businessId = this.auth?.pages?.[String(pageId)]?.businessId;
    if (businessId === null || businessId === undefined || businessId === '') return null;
    return String(businessId);
  }

  getAuthorizationToken(): string {
    if (!this.auth.jwt) {
      return '';
    }

    return `Bearer ${this.auth.jwt}`;
  }

  /**
   * Đồng bộ lại jwt (csrf) theo phiên BE hiện tại của cùng user.
   * Không phát onAuthChanged$ vì user/page/doanh nghiệp không đổi.
   */
  setJwt(jwt: string): void {
    if (this._isLoggingOut || !jwt || !this.auth?.uid) return;
    this.auth.jwt = jwt;
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(STORAGE_KEY.usr, JSON.stringify(this.auth));
    }
  }

  //----- Đoạn này xử lý đánh dấu đang xử lý hội thoại của page nào
  get pageDisplay() {
    return this._pageDisplay;
  }

  set pageDisplay(value: any) {
    let page = this.auth?.pages?.[value] ?? '';
    if (!page) {
      if (typeof sessionStorage !== "undefined") {
        const storageAffiliate = sessionStorage.getItem('pagesPartnerAfiliate')
        if (storageAffiliate) {
          const pagesPartnerAfiliate = JSON.parse(storageAffiliate)
          if (pagesPartnerAfiliate && pagesPartnerAfiliate?.length > 0) {
            for (const pagePartner of pagesPartnerAfiliate) {
              if (pagePartner.id == value) {
                page = pagePartner;
                break;
              }
            }
          }
        }
      }
    }
    this._pageDisplay.next(page);
  }

  /**
   * Lấy ID người dùng.
   */
  getUid(): string {
    return this.auth?.uid ?? '';
  }

  /**
   * Kiểm tra xem tài khoản hiện tại có phải là tài khoản test của Shopify hay không.
   * Tài khoản này cần được ẩn các tính năng mua gói cước/thanh toán để qua vòng duyệt app.
   * Parity FE13: auth.service.ts isShopifyReviewer()
   * @returns boolean true nếu là tài khoản test review của Shopify
   */
  isShopifyReviewer(): boolean {
    // UID của tài khoản demovpageacc@nhanh.vn được cung cấp để review
    const shopifyTestAccounts = ['PixZPKABBjkORK0sSSuX', 'ut7V25wBg6fP2ZhIivjf'];
    const uid = this.getUid();
    return !!uid && shopifyTestAccounts.includes(uid);
  }

  /**
   * Cập nhật lại tên hiển thị (displayName) trong memory và localStorage khi nhân viên được sửa tên.
   * @param displayName Tên hiển thị mới
   */
  updateUserDisplayName(displayName: string): void {
    if (!displayName || !this.auth) return;
    this.auth.displayName = displayName;
    if (isPlatformBrowser(this.platformId)) {
      try {
        localStorage.setItem(STORAGE_KEY.usr, JSON.stringify(this.auth));
      } catch (err) {
        console.warn('AuthService: Không thể cập nhật displayName vào localStorage', err);
      }
    }
    this._onAuthChanged$.next();
  }

  /**
   * Lấy thời gian tạo tài khoản người dùng.
   */
  getCreatedTime(): string {
    return this.auth?.createdTime ?? '';
  }

  /**
   * Lấy id thông báo hệ thống được xem lần cuối của user.
   */
  getLastAnnouncement(): string {
    return this.auth?.announcements ?? '';
  }

  /**
   * Lấy thời gian đăng của thông báo hệ thống được xem lần cuối.
   */
  getLastAnnouncementCreatedTime(): string | number {
    return this.auth?.announcementCreatedTime ?? '';
  }

  /**
   * Lưu thông báo hệ thống đã xem lần cuối để không tự mở lại popup sau khi reload trang (FE13 parity).
   * Không phát _onAuthChanged$ vì đây không phải thay đổi phiên làm việc.
   * @param announcementId ID thông báo đã xem
   * @param createdTime Thời gian đăng của thông báo
   */
  setLastAnnouncement(announcementId: string | number, createdTime?: string | number): void {
    // Đang đăng xuất / auth đã bị xoá → không ghi lại usr thiếu jwt vào localStorage
    if (this._isLoggingOut || !this.auth?.jwt) return;
    this.auth.announcements = String(announcementId);
    if (createdTime !== undefined && createdTime !== null) {
      this.auth.announcementCreatedTime = createdTime;
    }
    if (isPlatformBrowser(this.platformId)) {
      try {
        localStorage.setItem(STORAGE_KEY.usr, JSON.stringify(this.auth));
      } catch (err) {
        console.warn('AuthService: Không thể lưu thông báo đã xem vào localStorage', err);
      }
    }
  }

  // Lấy role của user hiện tại, ưu tiên lấy từ activeBusiness nếu có, nếu không thì lấy từ auth.role
  getCurrentRole(): number {
    if (this.hasJoinPages() && this.auth.businesses?.length) {
      const activeBusiness = this.getActiveBusiness();
      if (activeBusiness?.roleId) {
        // Nếu là custom role, trả về role cao nhất để có thể hiển thị menu
        if (this.isCustomRoleByRoleId(activeBusiness.roleId)) {
          return ROLES.Business_Staff; // Hoặc role phù hợp để hiển thị menu
        }
        return this.mapRoleIdToNumber(activeBusiness.roleId);
      }
    }

    return this.auth.role as number;
  }

  // Chuyển đổi roleId từ string sang số dạng cũ, nếu không tìm thấy thì trả về ROLES.Guest
  private mapRoleIdToNumber(roleId: string): number {
    const roleMapping: { [key: string]: number } = {
      'SYSTEM_ROLE_SUPER_ADMIN': ROLES.SuperAdmin,
      'SYSTEM_ROLE_ADMIN': ROLES.Admin,
      'SYSTEM_ROLE_MANAGER': ROLES.Business_Manager,
      'SYSTEM_ROLE_PAGE_ADMIN': ROLES.Page_Admin,
      'SYSTEM_ROLE_STAFF': ROLES.Business_Staff,
      'SYSTEM_ROLE_BLOG_EDITOR': ROLES.Blog_Editor,
      'SYSTEM_ROLE_MEMBER': ROLES.Member,
      'SYSTEM_ROLE_GUEST': ROLES.Guest
    };
    return roleMapping[roleId] || ROLES.Guest;
  }

  isAllowed(role: any): boolean {
    const currentRole = this.getCurrentRole();
    if (currentRole == ROLES.SuperAdmin) {
      return true;
    }
    let allowedRoles = [ROLES.Guest];
    // Init allowed roles
    if (currentRole == ROLES.Member) {
      allowedRoles.push(ROLES.Member);
    } else if (currentRole == ROLES.Business_Staff) {
      allowedRoles = [ROLES.Guest, ROLES.Member, ROLES.Business_Staff];
    } else if (currentRole == ROLES.Business_Manager) {
      allowedRoles = [ROLES.Guest, ROLES.Member, ROLES.Business_Staff, ROLES.Page_Admin, ROLES.Business_Manager];
    } else if (currentRole == ROLES.Page_Admin) {
      allowedRoles = [ROLES.Guest, ROLES.Member, ROLES.Business_Staff, ROLES.Page_Admin];
    } else if (currentRole == ROLES.Blog_Editor) {
      allowedRoles = [ROLES.Guest, ROLES.Member, ROLES.Business_Staff, ROLES.Blog_Editor];
    } else if (currentRole == ROLES.Admin) {
      allowedRoles = [ROLES.Guest, ROLES.Member, ROLES.Business_Staff, ROLES.Page_Admin, ROLES.Business_Manager, ROLES.Blog_Editor, ROLES.Admin];
    }

    return allowedRoles.includes(role);
  }

  isAdmin() {
    return this.auth.role == ROLES.Admin || this.auth.role == ROLES.SuperAdmin;
  }

  isSuperAdmin() {
    return this.auth.role === ROLES.SuperAdmin;
  }

  /**
   * Admin hệ thống (role 2), không phải Super Admin: menu Hệ thống chỉ gồm ADMIN_ALLOWED_SYSTEM_URLS.
   * Dùng role tài khoản, không dùng role của DN đang chọn (getCurrentRole): chủ DN có roleId SYSTEM_ROLE_SUPER_ADMIN
   * nên isAllowed() mở hết menu.
   */
  isAdminOnly() {
    return Number(this.auth?.role) === ROLES.Admin;
  }

  outPages() {
    if (this._isLoggingOut || !this.getAuthorizationToken()) return;
    this.io.leaveRoom(this.getPageIds());
    this.io.leaveAffRoom();

    delete this.auth.aBids;
    delete this.auth.pageIds;
    delete this.auth.pages;

    this._isSelectPage.next(false);
    this._pageDisplay.next('');

    // update logic
    if (isArray(this.auth.businesses)) {
      for (const business of this.auth.businesses) {
        business.pages = [];
      }
    }
    // this.cache.clearAll();

    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem('usr', JSON.stringify(this.auth));
      sessionStorage.removeItem('filterPageIds');
    }

    this.syncDurableObjectRooms();
  }

  /**
   * Lấy giá trị cài đặt cá nhân của người dùng từ auth.settings.
   * Các setting được load từ API /setting/user khi đăng nhập.
   * Giá trị thường là 1 (bật) / 2 (tắt).
   */
  getSetting(key: string): any {
    return this.auth?.settings?.[key];
  }

  /** Toàn bộ cài đặt cá nhân lưu trong auth (đăng nhập/localStorage), null nếu chưa có. */
  getSettings(): Record<string, unknown> | null {
    return this.auth?.settings ?? null;
  }

  /**
   * Đồng bộ bản cài đặt cá nhân mới nhất vào auth cache/localStorage.
   *
   * Một số feature khởi tạo sớm vẫn đọc `auth.settings` (trước khi API
   * `/user/setting` hoàn tất). Nếu chỉ cập nhật UserSettingsService thì các
   * feature đó sẽ tiếp tục dùng snapshot cũ cho đến lần đăng nhập kế tiếp.
   */
  updateUserSettings(settings: Record<string, unknown>): void {
    if (!this.isLoginEd()) return;
    this.auth.settings = {...(this.auth.settings ?? {}), ...settings};
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(STORAGE_KEY.usr, JSON.stringify(this.auth));
    }
  }

  /**
   * Lấy thông tin user đang đăng nhập, hiển thị lên header profile
   */
  getUser(): IUserHeader {
    const info: any = {}

    if (this.auth?.posUid) {
      info.type = 'POS';
      info.picture = 'default_avatar.png';
      info.displayName = this.auth?.displayName ?? '';

      if (this.auth?.fbUid) {
        info.fb = {
          picture: `https://graph.facebook.com/${this.auth.fbUid}/picture?height=100&width=100`,
          displayName: this.auth?.fbName ?? '',
        }
      }
    } else if (this.auth?.userType && this.auth.userType == 2) {
      info.displayName = this.auth.displayName;
    } else {
      info.type = 'FB';
      info.picture = `https://graph.facebook.com/${this.auth.fbUid}/picture?height=100&width=100`;
      info.displayName = this.auth?.fbName ?? '';
    }

    return info;
  }

  get isSelectPage() {
    return this._isSelectPage;
  }

  set isSelectPage(value: any) {
    this._isSelectPage.next(value);
  }

  /**
   * Check có phải là custom role không
   */
  isCustomRole(): boolean {
    const activeBusiness = this.getActiveBusiness();
    if (!activeBusiness?.roleId) return false;

    return this.isCustomRoleByRoleId(activeBusiness.roleId);
  }

  private isCustomRoleByRoleId(roleId: string): boolean {
    const systemRoles = Object.values(ROLES_NEW);
    return !systemRoles.includes(roleId);
  }

  // Hàm để lấy doanh nghiệp đang hoạt động, ưu tiên lấy từ localStorage nếu có, nếu không thì lấy từ auth.businesses
  // Nếu không có doanh nghiệp nào thì trả về null
  getActiveBusiness(): IBusinessOption | null {
    // Bước 1: Kiểm tra danh sách doanh nghiệp tồn tại
    if (!this.auth.businesses || (!isArray(this.auth.businesses) && typeof this.auth.businesses !== 'object')) {
      return null;
    }

    // Bước 2: Lấy toàn bộ danh sách doanh nghiệp mà người dùng thuộc về (chuẩn logic v1: dùng getBusinesses thay vì getBusinessesManagement)
    const allBusinesses = this.getBusinesses();
    if (!allBusinesses || allBusinesses.length === 0) return null;

    // Bước 3: Ưu tiên kiểm tra businessId từ URL query params (Dùng URLSearchParams để đọc ?businessId=)
    let urlBusinessId: string | null = null;
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      urlBusinessId = urlParams.get('businessId');
    }

    // Bước 4: Lấy từ localStorage nếu có
    let stored = null;
    let parsedBusiness = null;
    if (isPlatformBrowser(this.platformId)) {
      stored = localStorage.getItem('activeBusiness');
      if (stored) {
        try {
          parsedBusiness = JSON.parse(stored);
        } catch (e) {
          // Bỏ qua nếu dữ liệu rác để không làm crash luồng render menu
          localStorage.removeItem('activeBusiness');
        }
      }
    }

    // Bước 5: Xác định ID mục tiêu (ưu tiên URL > LocalStorage)
    const targetId = urlBusinessId || parsedBusiness?.id;

    if (targetId) {
      // Tìm business trong danh sách doanh nghiệp mà user thuộc về
      const currentBusiness = allBusinesses.find((b: { id: any; }) => b.id == targetId);

      if (currentBusiness) {
        // Nếu lấy từ URL -> Luôn tin tưởng vì đã check quyền ở trên
        // Nếu lấy từ LocalStorage -> Check thêm roleId để đảm bảo đồng bộ quyền
        if (urlBusinessId || currentBusiness?.roleId === parsedBusiness?.roleId) {
          if (isPlatformBrowser(this.platformId)) {
            localStorage.setItem('activeBusiness', JSON.stringify(currentBusiness));
          }
          return currentBusiness;
        }
      }

      // Hỗ trợ Admin: Nếu đang là Admin và có businessId trên URL, nhưng allBusinesses chưa kịp nạp (vừa F5)
      // thì ưu tiên lấy parsedBusiness từ localStorage nếu trùng khớp ID
      if (this.isAdmin() && urlBusinessId && parsedBusiness && String(parsedBusiness.id) === String(urlBusinessId)) {
        return parsedBusiness;
      }
    }

    // Bước 6: Fallback: Lấy doanh nghiệp đầu tiên trong danh sách user thuộc về (chuẩn theo logic v1)
    const firstBus = allBusinesses[0];
    if (isPlatformBrowser(this.platformId) && firstBus) {
      localStorage.setItem('activeBusiness', JSON.stringify(firstBus));
    }
    return firstBus;
  }

  // Hàm để set active business khi vào app
  initializeActiveBusiness() {
    if (!this.auth.businesses?.length || !this.auth.businesses[0]?.roleId) {
      return;
    }

    const stored = this.getActiveBusiness();
    if (stored) {
      this.activeBusiness = stored;
    } else if (this.auth.businesses?.length) {
      this.activeBusiness = this.auth.businesses[0];
    }
  }

  /**
     * Lấy tên người dùng đăng nhập
     */
    getUserDisplayName(): string {
        return this.auth.displayName ?? '';
    }
    getFbName() {
        return this.auth?.fbName ?? '';
    }
}
